import React, { useState, useEffect } from "react";
import { Container, Table, Button, Form, Row, Col, Alert, Spinner, Modal, Badge, ProgressBar } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { obtenerProductos, importarCsvOfertas, toggleOfertaProducto } from "../services/productoService";
import { useTipoCambio } from "../context/TipoCambioContext";
import apiClient from "../services/authService";

const ProductosAdmin = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editandoProducto, setEditandoProducto] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [importandoCsv, setImportandoCsv] = useState(false);
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [productoParaCategorizar, setProductoParaCategorizar] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("");
  const [showSubCategoriaModal, setShowSubCategoriaModal] = useState(false);
  const [productoParaSubCategorizar, setProductoParaSubCategorizar] = useState(null);
  const [subCategorias, setSubCategorias] = useState([]);
  const [subCategoriaSeleccionada, setSubCategoriaSeleccionada] = useState("");
  const [showReporteModal, setShowReporteModal] = useState(false);
  const [reporteImportacion, setReporteImportacion] = useState(null);
  const { formatearPrecioSoles } = useTipoCambio();
  const navigate = useNavigate();
  
  const [filtros, setFiltros] = useState({
    texto: "",
    marca: "",
    categoria: "",
    nroParte: "",
    nroSKU: ""
  });

  // Estados de paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [productosPorPagina, setProductosPorPagina] = useState(10);

  const [formProducto, setFormProducto] = useState({
    nroModelo: "",
    nroParte: "",
    nroSKU: "",
    producto: "",
    marca: "",
    modelo: "",
    descripcion: "",
    especificaciones: "",
    precio: "",
    stock: "",
    observaciones: "",
    foto1: "",
    foto2: "",
    foto3: "",
    foto4: "",
    linkHojaDeDatos: "",
    garantia: "",
    umGarantia: "años"
  });

  // Función para parsear el texto plano del reporte de importación
  const parsearReporteImportacion = (textoPlano) => {
    const texto = typeof textoPlano === 'string' ? textoPlano : String(textoPlano);
    
    // Extraer conteos del resumen: "X productos importados, Y errores"
    const importadosMatch = texto.match(/(\d+)\s*productos?\s*importados?/i);
    const erroresMatch = texto.match(/(\d+)\s*errores?/i);
    const cantImportados = importadosMatch ? parseInt(importadosMatch[1]) : 0;
    const cantErrores = erroresMatch ? parseInt(erroresMatch[1]) : 0;

    // Parsear cada línea de error
    const erroresDetalle = [];
    const lineas = texto.split('\n');
    
    lineas.forEach(linea => {
      if (!linea.includes('Error en l\u00ednea')) return;
      
      const lineaNumMatch = linea.match(/Error en l\u00ednea\s*(\d+)/i);
      const numLinea = lineaNumMatch ? lineaNumMatch[1] : '?';
      
      let motivo = linea;
      let tipo = 'error';
      
      if (linea.includes('Ya existe')) {
        tipo = 'duplicado';
        const nombreMatch = linea.match(/nombre:\s*(.+)$/i);
        motivo = nombreMatch ? nombreMatch[1].trim() : linea;
      } else {
        // Extraer solo la parte del mensaje después del último ":"
        const partes = linea.split(':');
        motivo = partes.length > 1 ? partes[partes.length - 1].trim() : linea;
      }
      
      erroresDetalle.push({ linea: numLinea, motivo, tipo });
    });

    return {
      importados: cantImportados,
      errores: cantErrores > 0 ? cantErrores : erroresDetalle.length,
      detalleErrores: erroresDetalle,
      total: cantImportados + (cantErrores > 0 ? cantErrores : erroresDetalle.length)
    };
  };

  useEffect(() => {
    cargarProductos();
    cargarCategorias();
    cargarSubCategorias();
  }, []);

  const cargarProductos = async () => {
    try {
      setLoading(true);
      const response = await obtenerProductos();
      setProductos(response.data);
      setError(null);
    } catch (err) {
      setError("Error al cargar los productos");
    } finally {
      setLoading(false);
    }
  };

  const cargarCategorias = async () => {
    try {
      const response = await apiClient.get('/categorias');
      setCategorias(response.data);
    } catch (err) {
      console.error("Error al cargar categorías:", err);
    }
  };

  const cargarSubCategorias = async () => {
    try {
      const response = await apiClient.get('/subcategorias');
      setSubCategorias(response.data);
    } catch (err) {
      console.error("Error al cargar subcategorías:", err);
    }
  };

  const abrirModalCategoria = (producto) => {
    setProductoParaCategorizar(producto);
    setCategoriaSeleccionada(producto.categoria?.idCategoria || "");
    setShowCategoriaModal(true);
  };

  const actualizarCategoria = async () => {
    if (!categoriaSeleccionada) {
      setMensaje({ tipo: "warning", texto: "Selecciona una categoría" });
      return;
    }
    
    try {
      await apiClient.patch(`/productos/${productoParaCategorizar.idProducto}/categoria`, {
        idCategoria: categoriaSeleccionada
      });
      setMensaje({ 
        tipo: "success", 
        texto: `Categoría actualizada para "${productoParaCategorizar.producto}"` 
      });
      setShowCategoriaModal(false);
      cargarProductos();
    } catch (err) {
      setMensaje({ 
        tipo: "danger", 
        texto: `Error al actualizar categoría: ${err.response?.data?.message || err.message}` 
      });
    }
  };

  const abrirModalSubCategoria = (producto) => {
    setProductoParaSubCategorizar(producto);
    setSubCategoriaSeleccionada(producto.subCategoria?.idSubCategoria || "");
    setShowSubCategoriaModal(true);
  };

  const actualizarSubCategoria = async () => {
    if (!subCategoriaSeleccionada) {
      setMensaje({ tipo: "warning", texto: "Selecciona una subcategoría" });
      return;
    }
    
    try {
      await apiClient.patch(`/productos/${productoParaSubCategorizar.idProducto}/subcategoria`, {
        idSubCategoria: subCategoriaSeleccionada
      });
      setMensaje({ 
        tipo: "success", 
        texto: `Subcategoría actualizada para "${productoParaSubCategorizar.producto}"` 
      });
      setShowSubCategoriaModal(false);
      cargarProductos();
    } catch (err) {
      setMensaje({ 
        tipo: "danger", 
        texto: `Error al actualizar subcategoría: ${err.response?.data?.message || err.message}` 
      });
    }
  };

  const productosFiltrados = productos.filter((prod) => {
    const texto = filtros.texto.toLowerCase();
    const parte = filtros.nroParte.toLowerCase();
    const sku = filtros.nroSKU.toLowerCase();
    return (
      (filtros.texto === "" ||
        prod.producto.toLowerCase().includes(texto) ||
        prod.descripcion?.toLowerCase().includes(texto) ||
        prod.marca.toLowerCase().includes(texto)) &&
      (filtros.marca === "" || prod.marca === filtros.marca) &&
      (filtros.categoria === "" || prod.categoria?.categoria === filtros.categoria) &&
      (parte === "" || prod.nroParte?.toLowerCase().includes(parte)) &&
      (sku === "" || prod.nroSKU?.toLowerCase().includes(sku))
    );
  });

  const marcasDisponibles = [...new Set(productos.map(p => p.marca))].sort();
  const categoriasDisponibles = [...new Set(productos.map(p => p.categoria?.categoria).filter(Boolean))].sort();

  // Cálculos de paginación
  const totalPaginas = Math.ceil(productosFiltrados.length / productosPorPagina);
  const indiceUltimo = paginaActual * productosPorPagina;
  const indicePrimero = indiceUltimo - productosPorPagina;
  const productosActuales = productosFiltrados.slice(indicePrimero, indiceUltimo);

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setPaginaActual(1);
  }, [filtros]);

  const cambiarPagina = (numeroPagina) => {
    setPaginaActual(numeroPagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const generarBotonesPaginacion = () => {
    const botones = [];
    const rango = 2; // Botones a cada lado de la página actual
    
    // Botón Primera página
    if (paginaActual > 1) {
      botones.push(
        <Button
          key="primero"
          variant="outline-primary"
          size="sm"
          onClick={() => cambiarPagina(1)}
          className="me-1"
        >
          ««
        </Button>
      );
    }
    
    // Botón Anterior
    if (paginaActual > 1) {
      botones.push(
        <Button
          key="anterior"
          variant="outline-primary"
          size="sm"
          onClick={() => cambiarPagina(paginaActual - 1)}
          className="me-1"
        >
          ‹
        </Button>
      );
    }
    
    // Botones de páginas
    let inicio = Math.max(1, paginaActual - rango);
    let fin = Math.min(totalPaginas, paginaActual + rango);
    
    // Mostrar puntos suspensivos al inicio
    if (inicio > 1) {
      botones.push(
        <span key="inicio-dots" className="mx-1 align-self-center">...</span>
      );
    }
    
    for (let i = inicio; i <= fin; i++) {
      botones.push(
        <Button
          key={i}
          variant={paginaActual === i ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => cambiarPagina(i)}
          className="me-1"
        >
          {i}
        </Button>
      );
    }
    
    // Mostrar puntos suspensivos al final
    if (fin < totalPaginas) {
      botones.push(
        <span key="fin-dots" className="mx-1 align-self-center">...</span>
      );
    }
    
    // Botón Siguiente
    if (paginaActual < totalPaginas) {
      botones.push(
        <Button
          key="siguiente"
          variant="outline-primary"
          size="sm"
          onClick={() => cambiarPagina(paginaActual + 1)}
          className="me-1"
        >
          ›
        </Button>
      );
    }
    
    // Botón Última página
    if (paginaActual < totalPaginas) {
      botones.push(
        <Button
          key="ultimo"
          variant="outline-primary"
          size="sm"
          onClick={() => cambiarPagina(totalPaginas)}
        >
          »»
        </Button>
      );
    }
    
    return botones;
  };

  const abrirModalEditar = (producto = null) => {
    if (producto) {
      setEditandoProducto(producto);
      setFormProducto({
        nroModelo: producto.nroModelo || "",
        nroParte: producto.nroParte || "",
        nroSKU: producto.nroSKU || "",
        producto: producto.producto || "",
        marca: producto.marca || "",
        modelo: producto.modelo || "",
        descripcion: producto.descripcion || "",
        especificaciones: producto.especificaciones || "",
        precio: producto.precio || "",
        stock: producto.stock || "",
        observaciones: producto.observaciones || "",
        foto1: producto.foto1 || "",
        foto2: producto.foto2 || "",
        foto3: producto.foto3 || "",
        foto4: producto.foto4 || "",
        linkHojaDeDatos: producto.linkHojaDeDatos || "",
        garantia: producto.garantia || "",
        umGarantia: producto.umGarantia || "años"
      });
    } else {
      setEditandoProducto(null);
      setFormProducto({
        nroModelo: "",
        nroParte: "",
        nroSKU: "",
        producto: "",
        marca: "",
        modelo: "",
        descripcion: "",
        especificaciones: "",
        precio: "",
        stock: "",
        observaciones: "",
        foto1: "",
        foto2: "",
        foto3: "",
        foto4: "",
        linkHojaDeDatos: "",
        garantia: "",
        umGarantia: "años"
      });
    }
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    setFormProducto({ ...formProducto, [e.target.name]: e.target.value });
  };

  const guardarProducto = async (e) => {
    e.preventDefault();
    try {
      if (editandoProducto) {
        await apiClient.put(`/productos/${editandoProducto.idProducto}`, formProducto);
      } else {
        await apiClient.post("/productos", formProducto);
      }
      
      setMensaje({ 
        tipo: "success", 
        texto: editandoProducto ? "Producto actualizado exitosamente" : "Producto creado exitosamente" 
      });
      
      setShowModal(false);
      cargarProductos();
    } catch (err) {
      setMensaje({ 
        tipo: "danger", 
        texto: `Error al ${editandoProducto ? 'actualizar' : 'crear'} el producto: ${err.response?.data?.message || err.message}` 
      });
    }
  };

  const eliminarProducto = async (id) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este producto?")) {
      try {
        await apiClient.delete(`/productos/${id}`);
        setMensaje({ tipo: "success", texto: "Producto eliminado exitosamente" });
        cargarProductos();
      } catch (err) {
        setMensaje({ tipo: "danger", texto: `Error al eliminar el producto: ${err.response?.data?.message || err.message}` });
      }
    }
  };

  const handleToggleOferta = async (producto) => {
    try {
      const nuevoEstado = !producto.esOferta;
      await toggleOfertaProducto(producto.idProducto, nuevoEstado);
      setMensaje({ 
        tipo: "success", 
        texto: nuevoEstado 
          ? `"${producto.producto}" marcado como oferta` 
          : `"${producto.producto}" ya no es oferta` 
      });
      cargarProductos();
    } catch (err) {
      setMensaje({ 
        tipo: "danger", 
        texto: `Error al cambiar estado de oferta: ${err.response?.data?.message || err.message}` 
      });
    }
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status" />
        <div>Cargando productos...</div>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <h2>Gestionar Productos</h2>
      
      {mensaje && <Alert variant={mensaje.tipo} dismissible onClose={() => setMensaje(null)}>{mensaje.texto}</Alert>}
      
      {/* Botón para volver al panel de administrador */}
      <Button variant="primary" className="mb-3" onClick={() => navigate("/admin")}>
        Volver al Panel de Administrador
      </Button>

      {/* Filtros y acciones mejorados */}
      <div className="mb-4 p-3 bg-light rounded shadow-sm">
        {/* Fila de búsqueda y filtros */}
        <Row className="mb-3 align-items-center">
          <Col md={12}>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <div style={{ flex: '1 1 250px', minWidth: '200px' }}>
                <Form.Control
                  type="text"
                  placeholder="🔍 Buscar por nombre, descripción o marca..."
                  value={filtros.texto}
                  onChange={(e) => setFiltros({ ...filtros, texto: e.target.value })}
                  className="shadow-sm"
                />
              </div>
              <div style={{ flex: '0 1 180px', minWidth: '150px' }}>
                <Form.Control
                  type="text"
                  placeholder="Nro. de parte"
                  value={filtros.nroParte}
                  onChange={(e) => setFiltros({ ...filtros, nroParte: e.target.value })}
                  className="shadow-sm"
                />
              </div>
              <div style={{ flex: '0 1 160px', minWidth: '150px' }}>
                <Form.Control
                  type="text"
                  placeholder="SKU"
                  value={filtros.nroSKU}
                  onChange={(e) => setFiltros({ ...filtros, nroSKU: e.target.value })}
                  className="shadow-sm"
                />
              </div>
              <div style={{ flex: '0 1 200px', minWidth: '150px' }}>
                <Form.Select
                  value={filtros.marca}
                  onChange={(e) => setFiltros({ ...filtros, marca: e.target.value })}
                  className="shadow-sm"
                >
                  <option value="">🏷️ Todas las marcas</option>
                  {marcasDisponibles.map((marca) => (
                    <option key={marca} value={marca}>{marca}</option>
                  ))}
                </Form.Select>
              </div>
              <div style={{ flex: '0 1 200px', minWidth: '150px' }}>
                <Form.Select
                  value={filtros.categoria}
                  onChange={(e) => setFiltros({ ...filtros, categoria: e.target.value })}
                  className="shadow-sm"
                >
                  <option value="">📂 Todas las categorías</option>
                  {categoriasDisponibles.map((categoria) => (
                    <option key={categoria} value={categoria}>{categoria}</option>
                  ))}
                </Form.Select>
              </div>
              {(filtros.texto || filtros.marca || filtros.categoria || filtros.nroParte || filtros.nroSKU) && (
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={() => setFiltros({ texto: "", marca: "", categoria: "", nroParte: "", nroSKU: "" })}
                  title="Limpiar filtros"
                >
                  ✕ Limpiar
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {/* Fila de acciones */}
        <Row className="align-items-center">
          <Col md={12}>
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Button variant="secondary" onClick={cargarProductos}>
                  🔄 Refrescar
                </Button>
              </div>
              
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => setCsvFile(e.target.files[0] || null)}
                    style={{ width: '200px' }}
                    size="sm"
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={async () => {
                      if (!csvFile) {
                        setMensaje({ tipo: 'danger', texto: 'Selecciona un archivo CSV primero.' });
                        return;
                      }
                      setImportandoCsv(true);
                      setMensaje(null);
                      try {
                        const formData = new FormData();
                        formData.append('archivo', csvFile);
                        const resp = await apiClient.post('/productos/importar-csv', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                          transformResponse: [(data) => data] // Forzar texto plano, no parsear JSON
                        });
                        
                        const reporte = parsearReporteImportacion(resp.data);
                        setReporteImportacion(reporte);
                        setShowReporteModal(true);
                        setCsvFile(null);
                        if (reporte.importados > 0) {
                          cargarProductos();
                        }
                      } catch (err) {
                        console.error('Error importando CSV:', err);
                        // El backend puede devolver el reporte como error HTTP también
                        const errData = err.response?.data;
                        if (errData) {
                          const texto = typeof errData === 'string' ? errData : JSON.stringify(errData);
                          if (texto.includes('importad') || texto.includes('Error en línea') || texto.includes('errores')) {
                            const reporte = parsearReporteImportacion(texto);
                            setReporteImportacion(reporte);
                            setShowReporteModal(true);
                            setCsvFile(null);
                            if (reporte.importados > 0) cargarProductos();
                            return;
                          }
                        }
                        setMensaje({ tipo: 'danger', texto: `Error al importar CSV: ${err.response?.data || err.message}` });
                      } finally {
                        setImportandoCsv(false);
                      }
                    }}
                    disabled={importandoCsv}
                  >
                    {importandoCsv ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-1"/>
                        Importando...
                      </>
                    ) : (
                      '📄 Importar CSV'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Contador de resultados y selector de productos por página */}
        <Row className="mt-2">
          <Col md={6}>
            <small className="text-muted">
              Mostrando <strong>{indicePrimero + 1}</strong> a <strong>{Math.min(indiceUltimo, productosFiltrados.length)}</strong> de <strong>{productosFiltrados.length}</strong> productos filtrados (Total: <strong>{productos.length}</strong>)
            </small>
          </Col>
          <Col md={6} className="text-end">
            <div className="d-inline-flex align-items-center gap-2">
              <small className="text-muted">Mostrar:</small>
              <Form.Select
                size="sm"
                value={productosPorPagina}
                onChange={(e) => {
                  setProductosPorPagina(Number(e.target.value));
                  setPaginaActual(1);
                }}
                style={{ width: 'auto' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
              <small className="text-muted">por página</small>
            </div>
          </Col>
        </Row>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Tabla de productos */}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>Producto</th>
            <th>Marca</th>
            <th>Modelo</th>
            <th>Precio</th>
            <th>Stock</th>
            <th>Estado</th>
            <th>Oferta</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {productosActuales.map((producto) => (
            <tr key={producto.idProducto}>
              <td>{producto.idProducto}</td>
              <td>{producto.producto}</td>
              <td>{producto.marca}</td>
              <td>{producto.modelo}</td>
              <td>{formatearPrecioSoles(producto.precio || 0)}</td>
              <td>{producto.stock}</td>
              <td>
                <span className={`badge ${producto.estadoProducto?.estadoProd === 'Activo' ? 'bg-success' : 'bg-danger'}`}>
                  {producto.estadoProducto?.estadoProd || 'N/A'}
                </span>
              </td>
              <td className="text-center">
                <Form.Check
                  type="checkbox"
                  checked={producto.esOferta || false}
                  onChange={() => handleToggleOferta(producto)}
                  title={producto.esOferta ? 'Quitar de ofertas' : 'Marcar como oferta'}
                />
              </td>
              <td>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="me-2"
                  onClick={() => abrirModalEditar(producto)}
                >
                  Editar
                </Button>
                <Button 
                  variant="outline-warning" 
                  size="sm" 
                  className="me-2"
                  onClick={() => abrirModalCategoria(producto)}
                >
                  Categoría
                </Button>
                <Button 
                  variant="outline-info" 
                  size="sm" 
                  className="me-2"
                  onClick={() => abrirModalSubCategoria(producto)}
                >
                  SubCategoría
                </Button>
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => eliminarProducto(producto.idProducto)}
                >
                  Eliminar
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Controles de paginación */}
      {totalPaginas > 1 && (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <small className="text-muted">
              Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong>
            </small>
          </div>
          <div className="d-flex align-items-center">
            {generarBotonesPaginacion()}
          </div>
          <div>
            <Form.Control
              type="number"
              size="sm"
              min="1"
              max={totalPaginas}
              value={paginaActual}
              onChange={(e) => {
                const pagina = Number(e.target.value);
                if (pagina >= 1 && pagina <= totalPaginas) {
                  cambiarPagina(pagina);
                }
              }}
              style={{ width: '80px' }}
              placeholder="Ir a..."
            />
          </div>
        </div>
      )}

      {/* Modal para editar/crear producto */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editandoProducto ? 'Editar' : 'Crear'} Producto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={guardarProducto}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre del Producto</Form.Label>
                  <Form.Control
                    type="text"
                    name="producto"
                    value={formProducto.producto}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marca</Form.Label>
                  <Form.Control
                    type="text"
                    name="marca"
                    value={formProducto.marca}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Modelo</Form.Label>
                  <Form.Control
                    type="text"
                    name="modelo"
                    value={formProducto.modelo}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>N° Parte</Form.Label>
                  <Form.Control
                    type="text"
                    name="nroParte"
                    value={formProducto.nroParte}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>SKU</Form.Label>
                  <Form.Control
                    type="text"
                    name="nroSKU"
                    value={formProducto.nroSKU}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>N° Modelo</Form.Label>
                  <Form.Control
                    type="text"
                    name="nroModelo"
                    value={formProducto.nroModelo}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Precio (USD)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="precio"
                    value={formProducto.precio}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Stock</Form.Label>
                  <Form.Control
                    type="number"
                    name="stock"
                    value={formProducto.stock}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="descripcion"
                value={formProducto.descripcion}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Especificaciones</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="especificaciones"
                value={formProducto.especificaciones}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Garantía</Form.Label>
                  <Form.Control
                    type="number"
                    name="garantia"
                    value={formProducto.garantia}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unidad de Garantía</Form.Label>
                  <Form.Select
                    name="umGarantia"
                    value={formProducto.umGarantia}
                    onChange={handleInputChange}
                  >
                    <option value="días">Días</option>
                    <option value="meses">Meses</option>
                    <option value="años">Años</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>URL Foto 1</Form.Label>
              <Form.Control
                type="url"
                name="foto1"
                value={formProducto.foto1}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Link Hoja de Datos</Form.Label>
              <Form.Control
                type="url"
                name="linkHojaDeDatos"
                value={formProducto.linkHojaDeDatos}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Observaciones</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="observaciones"
                value={formProducto.observaciones}
                onChange={handleInputChange}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit">
                {editandoProducto ? 'Actualizar' : 'Crear'} Producto
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal para cambiar categoría */}
      <Modal show={showCategoriaModal} onHide={() => setShowCategoriaModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar Categoría</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {productoParaCategorizar && (
            <>
              <p><strong>Producto:</strong> {productoParaCategorizar.producto}</p>
              <p><strong>Categoría actual:</strong> {productoParaCategorizar.categoria?.categoria || 'Sin categoría'}</p>
              
              <Form.Group className="mb-3">
                <Form.Label>Nueva Categoría</Form.Label>
                <Form.Select
                  value={categoriaSeleccionada}
                  onChange={(e) => setCategoriaSeleccionada(e.target.value)}
                  required
                >
                  <option value="">Selecciona una categoría</option>
                  {categorias.map((cat) => (
                    <option key={cat.idCategoria} value={cat.idCategoria}>
                      {cat.categoria}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCategoriaModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={actualizarCategoria}>
            Actualizar Categoría
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para cambiar subcategoría */}
      <Modal show={showSubCategoriaModal} onHide={() => setShowSubCategoriaModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar Subcategoría</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {productoParaSubCategorizar && (
            <>
              <p><strong>Producto:</strong> {productoParaSubCategorizar.producto}</p>
              <p><strong>Subcategoría actual:</strong> {productoParaSubCategorizar.subCategoria?.subCategoria || 'Sin subcategoría'}</p>
              
              <Form.Group className="mb-3">
                <Form.Label>Nueva Subcategoría</Form.Label>
                <Form.Select
                  value={subCategoriaSeleccionada}
                  onChange={(e) => setSubCategoriaSeleccionada(e.target.value)}
                  required
                >
                  <option value="">Selecciona una subcategoría</option>
                  {subCategorias.map((subCat) => (
                    <option key={subCat.idSubCategoria} value={subCat.idSubCategoria}>
                      {subCat.subCategoria}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubCategoriaModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={actualizarSubCategoria}>
            Actualizar Subcategoría
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Reporte de Importación */}
      <Modal show={showReporteModal} onHide={() => setShowReporteModal(false)} size="lg" scrollable>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>📊 Reporte de Importación CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reporteImportacion && (
            <>
              {/* Resumen general */}
              <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <Row className="text-center">
                  <Col md={4}>
                    <div className="p-3">
                      <h3 className="mb-1">{reporteImportacion.total}</h3>
                      <small className="text-muted">Total procesados</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3">
                      <h3 className="mb-1 text-success">{reporteImportacion.importados}</h3>
                      <small className="text-muted">✅ Importados</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3">
                      <h3 className="mb-1 text-danger">{reporteImportacion.errores}</h3>
                      <small className="text-muted">❌ Con errores</small>
                    </div>
                  </Col>
                </Row>
                <ProgressBar className="mt-2" style={{ height: '8px' }}>
                  <ProgressBar 
                    variant="success" 
                    now={reporteImportacion.total > 0 ? (reporteImportacion.importados / reporteImportacion.total) * 100 : 0} 
                    key={1} 
                  />
                  <ProgressBar 
                    variant="danger" 
                    now={reporteImportacion.total > 0 ? (reporteImportacion.errores / reporteImportacion.total) * 100 : 0} 
                    key={2} 
                  />
                </ProgressBar>
                <div className="d-flex justify-content-between mt-1">
                  <small className="text-success">
                    {reporteImportacion.total > 0 ? ((reporteImportacion.importados / reporteImportacion.total) * 100).toFixed(1) : 0}% éxito
                  </small>
                  <small className="text-danger">
                    {reporteImportacion.total > 0 ? ((reporteImportacion.errores / reporteImportacion.total) * 100).toFixed(1) : 0}% errores
                  </small>
                </div>
              </div>

              {/* Estado general */}
              {reporteImportacion.importados > 0 && reporteImportacion.errores === 0 && (
                <Alert variant="success">
                  🎉 ¡Todos los productos se importaron correctamente!
                </Alert>
              )}
              {reporteImportacion.importados > 0 && reporteImportacion.errores > 0 && (
                <Alert variant="warning">
                  ⚠️ Importación parcial: {reporteImportacion.importados} productos importados, {reporteImportacion.errores} con errores.
                </Alert>
              )}
              {reporteImportacion.importados === 0 && reporteImportacion.errores > 0 && (
                <Alert variant="danger">
                  ❌ No se importó ningún producto. Todos los registros presentaron errores.
                </Alert>
              )}

              {/* Detalle de errores */}
              {reporteImportacion.detalleErrores.length > 0 && (
                <>
                  <h6 className="mt-3 mb-2">
                    Detalle de errores 
                    <Badge bg="danger" className="ms-2">{reporteImportacion.detalleErrores.length}</Badge>
                  </h6>
                  <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                    <Table size="sm" bordered hover className="mb-0">
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                        <tr>
                          <th style={{ width: '70px' }}>Línea</th>
                          <th style={{ width: '100px' }}>Tipo</th>
                          <th>Producto / Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reporteImportacion.detalleErrores.map((err, idx) => (
                          <tr key={idx}>
                            <td className="text-center">
                              <Badge bg="secondary">{err.linea}</Badge>
                            </td>
                            <td>
                              {err.tipo === 'duplicado' ? (
                                <Badge bg="warning" text="dark">🔁 Duplicado</Badge>
                              ) : (
                                <Badge bg="danger">❌ Error</Badge>
                              )}
                            </td>
                            <td>
                              <small style={{ wordBreak: 'break-word' }}>{err.motivo}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReporteModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="mt-3">
        <Link to="/admin" className="btn btn-secondary">
          Volver al Panel de Administrador
        </Link>
      </div>
    </Container>
  );
};

export default ProductosAdmin;
